import type { FormApi } from 'final-form';
import type { PropsWithChildren } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ButtonV3 } from '../../../components/Button/ButtonV3';
import { SubmitButtonV3 } from '../../../components/FinalForm/SubmitButtonV3';
import { GraphModal } from '../../../components/GraphModal/GraphModal';
import { confirmationToast, failureToast } from '../../../components/Toast';
import { adminDeleteComment } from '../../../contributor/api/admin';
import { ReactComponent as WarningIcon } from '../../../icons/icon-warning.svg';
import type { DeleteResponseStatusFragment } from '../../api/message';
import { AdminRemoveContent } from '../../Remove/AdminRemoveContent';

const messages = defineMessages({
  title: { defaultMessage: "Moderator's removal of a message", id: '1lHbpx' },
  confirmationLabel: { defaultMessage: 'the message has been deleted', id: 'hUXU/+' },
  failureLabel: { defaultMessage: 'Unable to delete the message', id: 'eb8URm' },
  refuse: { defaultMessage: 'Yes, keep the discussion', id: 'pBRjFg' },
  remove: { defaultMessage: 'No, delete the entire discussion', id: 'yQVW3q' },
  removeNoChild: { defaultMessage: 'Delete the comment', id: 'SICOMK' },
  warning: {
    defaultMessage: `This comment has responses that will be deleted with it. Do you wish to continue displaying them by replacing this comment with 'This comment has been removed by the moderator'?`,
    id: '7cBwUX',
  },
  confirmDeleteNoChild: {
    defaultMessage: `Are you sure you want to do this?`,
    id: 'dclZr/',
  },
  keep: {
    defaultMessage: `Cancel`,
    id: '47FYwb',
  },
});

export type AdminMessageRemoverProps = {
  messageId: string;
  threadId: string;
  websiteId: string;
  content: string;
  status: string;
  hasChildren: boolean;
  onRemove: (data: DeleteResponseStatusFragment) => void;
  asModerator?: boolean;
};
export const AdminMessageRemover: React.FC<PropsWithChildren<AdminMessageRemoverProps>> = ({
  messageId,
  threadId,
  websiteId,
  content,
  status,
  onRemove,
  hasChildren,
  children,
}) => {
  const remove = useCallback(
    async values => {
      try {
        const data = await adminDeleteComment({
          commentId: messageId,
          threadId,
          websiteId,
          ...values,
        });
        confirmationToast(<FormattedMessage {...messages.confirmationLabel} />);
        onRemove?.(data);
      } catch {
        failureToast(<FormattedMessage {...messages.failureLabel} />);
      }
    },
    [onRemove, messageId, threadId, websiteId],
  );

  const lazyContent = useCallback(
    tip => {
      const preserveDiscussion = (_: unknown, form: FormApi) => {
        form.change('preserveDiscussion', true);
      };
      const removeDiscussion = (_: unknown, form: FormApi) => {
        form.change('preserveDiscussion', false);
      };

      return (
        <AdminRemoveContent
          messages={messages}
          content={content}
          status={status}
          warning={
            hasChildren ? (
              <div className="flex space-x-3">
                <WarningIcon className="w-8 h-8 text-white bg-red-300 p-2 rounded mt-1.5" />
                <span className="flex-1">
                  <FormattedMessage {...messages.warning} />
                </span>
              </div>
            ) : (
              <FormattedMessage {...messages.confirmDeleteNoChild} />
            )
          }
          remove={async values => {
            await remove(values);
            tip.hide();
          }}
          cancel={tip.hide}
          actions={
            <div className="flex space-x-2 my-4">
              {hasChildren ? (
                <>
                  <SubmitButtonV3 onClick={preserveDiscussion} className="grow" variant="contained">
                    <FormattedMessage {...messages.refuse} />
                  </SubmitButtonV3>
                  <SubmitButtonV3 onClick={removeDiscussion} className="grow" variant="contained">
                    <FormattedMessage {...messages.remove} />
                  </SubmitButtonV3>
                </>
              ) : (
                <>
                  <ButtonV3 onClick={tip.hide} className="grow" variant={'text'}>
                    <FormattedMessage {...messages.keep} />
                  </ButtonV3>
                  <SubmitButtonV3 onClick={removeDiscussion} className="grow" variant="contained">
                    <FormattedMessage {...messages.removeNoChild} />
                  </SubmitButtonV3>
                </>
              )}
            </div>
          }
        />
      );
    },
    [remove, content, status, hasChildren],
  );

  return (
    <GraphModal maxWidth={800} lazyContent={lazyContent} showPageOverlay forceModal>
      {children}
    </GraphModal>
  );
};
